import React from 'react';
import { Button, Card, Input, List } from 'antd';
import { CancelToken } from '../api/HttpClient';
import { AccessTokenInfo, BankLoginSummary, TransactionApiV2 } from '../api/TransactionApi';
import { logger } from '../utils';

interface ListItemComponentProps {
    accessTokenExpired: boolean,
    item: { mm: string, updatedAt: string, updatedAfterEom: boolean },
    reloadTransactions: (mm: string) => Promise<void>,
}

class ListItem extends React.Component<ListItemComponentProps, { reloading: boolean }> {
    constructor(props: ListItemComponentProps) {
        super(props)

        const today = new Date()
        this.state = {
            reloading: false,
        };
    }


    reloadTransactions(reloadMm: string) {

        const { reloadTransactions } = this.props
        this.setState({ reloading: true })
        reloadTransactions(reloadMm)
            .finally(() => {
                this.setState({ reloading: false })
            });
    }

    render() {
        const { mm, updatedAt, updatedAfterEom } = this.props.item

        const updatedAtDate = new Date(updatedAt)

        const emoji = updatedAfterEom
            ? '✅'
            : +mm === new Date().getMonth() + 1 ? '⏳' : '❌'

        return (
            <List.Item actions={[
            ]}>
                <div><b>{mm}:</b> {updatedAt} {emoji}
                    {!this.state.reloading && !this.props.accessTokenExpired
                        && <Button onClick={() => this.reloadTransactions(mm)}>Reload</Button>}
                </div>
            </List.Item>
        )
    }
}

interface AccessTokenComponentProps {
    yyyy: string;
    accountId: string;
    tokenExpiry: string;
    lastUpdatedDates: { [mm: string]: { updatedAt: string } }
    recreateReport: () => void;
    reloadAccessToken: () => void;
}

interface AccessTokenComponentState {
    loginSummary?: BankLoginSummary
    error?: string
    updatingTransactions: boolean
    attemptedRefreshError?: string
    isRefreshing: boolean
    showMonths: boolean
}

class AccessTokenComponent extends React.Component<AccessTokenComponentProps, AccessTokenComponentState> {
    constructor(props: AccessTokenComponentProps) {
        super(props)

        const today = new Date()
        const thisMonth = ((today.getMonth() + 1) + '').padStart(2, '0')
        this.state = {
            updatingTransactions: false,
            isRefreshing: false,
            showMonths: false,
        };
    }

    private cancelToken?: CancelToken
    private _isCanceled: boolean = false

    refresh() {
        const { accountId, reloadAccessToken } = this.props
        const { isRefreshing } = this.state
        if (isRefreshing) return
        this.setState({ isRefreshing: true })
        TransactionApiV2.bank.refresh(accountId)
            .then(response => {
                if (response.statusCode === 200) {
                    reloadAccessToken()
                } else {
                    this.setState({ isRefreshing: false, attemptedRefreshError: (response.data as any).error_code || ':(' })
                }
            })
            .catch(err => {
                logger.error('exchangeCode error', err);
            });
    }

    async reloadTransactions(reloadMm: string) {
        if (/^\d{2}$/.exec(reloadMm) == null || +reloadMm < 1 || +reloadMm > 12) {
            console.log("Must be 01 to 12", reloadMm)
            return
        }

        const { accountId, recreateReport, yyyy, reloadAccessToken } = this.props
        const reloadYyyyMm = `${yyyy}-${reloadMm}`
        console.log("reloading", reloadYyyyMm)
        const response = await TransactionApiV2.bank.updateTransactions(accountId, reloadYyyyMm)

        logger.info('got latest transactions', response);
        reloadAccessToken()
        recreateReport()
    }

    componentWillUnmount() {
        this._isCanceled = true;
        this.cancelToken?.cancel();
    }

    render() {
        const { tokenExpiry, lastUpdatedDates, yyyy } = this.props
        const {
            error,
            attemptedRefreshError,
            isRefreshing,
            showMonths,
        } = this.state

        if (error) {
            return <>Error: {error}</>
        }

        const accessTokenDate = new Date(tokenExpiry)
        const accessTokenExpired = new Date().getTime() - accessTokenDate.getTime() > 0
        const accesstokenExpiry = accessTokenDate.toLocaleString()

        const thisYear = new Date().getFullYear()
        const isThisYear = +yyyy === thisYear
        const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']

        const thisMonth = new Date().getMonth() + 1
        const tableData = months
            .filter(mm => !isThisYear || +mm <= thisMonth)
            .map(mm => {
                const item = lastUpdatedDates[mm]
                function getUpdatedAfterEom() {
                    if (!item) return false
                    const updatedAtDate = new Date(item.updatedAt)
                    const updatedAfterEom = updatedAtDate.getFullYear() > +yyyy || updatedAtDate.getMonth() + 1 > +mm
                    return updatedAfterEom
                }
                const updatedAfterEom = getUpdatedAfterEom();
                return { ...item, mm, updatedAfterEom }
            })

        const everythingUpToDate = tableData.every(item => item.updatedAfterEom) && tableData.length === 12

        return <div>
            {accessTokenExpired && <>
                <div>Access token expired at <b>{accesstokenExpiry}</b></div>
                {!attemptedRefreshError && <>
                    {isRefreshing && <div>waiting</div>}
                    {!isRefreshing && <Button onClick={() => this.refresh()}>Refresh Token</Button>}
                </>}
                {!!attemptedRefreshError && <>
                    <div>Could not refresh: {attemptedRefreshError}</div>
                    <div>Create new access token below</div>
                </>}
            </>}
            {everythingUpToDate && <>
                <b style={{ marginRight: '10px' }}>Everything up to date 🎉</b>
                <Button onClick={() => this.setState({ showMonths: !showMonths })}>{showMonths ? 'Hide' : 'Show'} months</Button>
            </>}
            {!accessTokenExpired && <>
                <div>Access token expires at <b>{accesstokenExpiry}</b></div>
            </>}
            {(!everythingUpToDate || showMonths) && <List
                itemLayout="vertical"
                dataSource={tableData}
                renderItem={(item, index) => <ListItem
                    accessTokenExpired={accessTokenExpired}
                    item={item}
                    reloadTransactions={mm => this.reloadTransactions(mm)}
                />}
            />}
        </div>
    }
}

export interface ReportAccessTokenComponentProps {
    yyyy: string;
    accountId: string;
    recreateReport: () => void;
}

export interface ReportAccessTokenComponentState {
    loginSummary?: BankLoginSummary
    error?: string
    updatingTransactions: boolean
    attemptedRefreshError?: string
}

export class ReportAccessTokenComponent extends React.Component<ReportAccessTokenComponentProps, ReportAccessTokenComponentState> {
    constructor(props: ReportAccessTokenComponentProps) {
        super(props)
        this.state = {
            updatingTransactions: false,
        };
    }

    componentDidMount(): void {
        this.loadLoginStatus()
    }

    private cancelToken?: CancelToken
    private _isCanceled: boolean = false

    loadLoginStatus() {
        this.cancelToken = new CancelToken();
        this._isCanceled = false
        const { yyyy, accountId } = this.props
        TransactionApiV2.bank.summary(accountId, yyyy, this.cancelToken)
            .then(response => {
                if (response.statusCode === 200) {
                    this.setState({
                        loginSummary: response.data
                    });
                } else {
                    console.log(response)
                    this.setState({ error: (response.data as any).message || ':(' })
                }
            })
            .catch(err => {
                if (!this._isCanceled) {
                    logger.warn('get transactions cancelled', err);
                }
            });
    }

    componentWillUnmount() {
        this._isCanceled = true;
        this.cancelToken?.cancel();
    }

    render() {
        const { accountId, yyyy, recreateReport } = this.props
        const { loginSummary, error } = this.state

        if (error) {
            return <>Error: {error}</>
        }

        if (!loginSummary) {
            return <>Waiting</>
        }

        const foundAccount = loginSummary.account

        return <>
            {!!foundAccount && <AccessTokenComponent
                key={foundAccount.account_id}
                yyyy={yyyy}
                accountId={accountId}
                tokenExpiry={foundAccount.access_token_expiry}
                recreateReport={recreateReport}
                lastUpdatedDates={loginSummary.transactions}
                reloadAccessToken={() => this.loadLoginStatus()} />}
            {!foundAccount && <div>Account not found, try linking a new token</div>}
        </>
    }
}
